import React from 'react';

/**
 * Wraps the native `<input type="file">` boilerplate so callers can trigger
 * the OS file picker from any button. The hook returns:
 *
 * - `open()`: imperatively opens the picker (call from a click handler).
 * - `inputProps`: spread onto a hidden `<input type="file" />` rendered
 *   anywhere in the tree (we don't render the element itself here so the
 *   caller controls placement and can keep this hook UI-agnostic).
 *
 * The input's value is cleared on every change so re-picking the same file
 * still fires `onChange` (browsers suppress identical selections otherwise).
 */
export function useFilePicker(args: {
  onFiles: (files: File[]) => void;
  /** Forwarded to the `accept` attribute. */
  accept?: string;
  /** Defaults to `true`. */
  multiple?: boolean;
}): {
  open: () => void;
  inputProps: {
    ref: React.RefObject<HTMLInputElement>;
    type: 'file';
    accept?: string;
    multiple: boolean;
    className: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  };
} {
  const { onFiles, accept, multiple = true } = args;
  const ref = React.useRef<HTMLInputElement>(null);

  const onFilesRef = React.useRef(onFiles);
  React.useEffect(() => {
    onFilesRef.current = onFiles;
  }, [onFiles]);

  const open = React.useCallback(() => {
    ref.current?.click();
  }, []);

  const onChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.currentTarget.files ?? []);
    // Reset so the same file can be picked twice in a row.
    e.currentTarget.value = '';
    if (files.length > 0) {
      onFilesRef.current(files);
    }
  }, []);

  return {
    open,
    inputProps: {
      ref,
      type: 'file',
      accept,
      multiple,
      className: 'hidden',
      onChange
    }
  };
}
